import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal } from "antd";
import { useRouter } from "next/router";
import { useDebounce } from "usehooks-ts";
import { SearchInput } from "src/components/home/SearchInput";
import { AutoCompleteResults } from "../../pages/[orgId]/explore/AutoCompleteResults";
import { useSearch } from "src/search/useSearchContext";
import {
  useExplore,
  ContextSource,
} from "../../pages/[orgId]/explore/useExploreContext";
import { serializeDocument } from "../../pages/[orgId]/explore/lib/useExploreDocument";
import { serializeTabs } from "../../pages/[orgId]/explore/lib/useTabs";
import { serializeParams } from "src/search/lib/params-state-reducer";
import { DeepLinkType } from "../../pages/api/deep-links/types";
import { useApplicationContext } from "src/hooks/useApplication";

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  topics?: any[];
}

export const SearchModal: React.FC<SearchModalProps> = ({
  open,
  onClose,
  orgId,
  topics,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isFocused, setIsFocused] = useState(false);
  const searchInputRef = useRef<any>(null);
  const router = useRouter();
  const searchContext = useSearch({ orgId, search: debouncedSearchTerm });
  const exploreContext = useExplore(searchContext, {}, ContextSource.Ask);
  const app = useApplicationContext();

  useEffect(() => {
    exploreContext.ask.setSearchTerm(searchTerm);
  }, [searchTerm, exploreContext.ask]);

  const handleSubmitSearch = useCallback(
    (term: string) => {
      const url = `/[orgId]/explore?orgId=${orgId}&s=${
        searchContext.params.serialized
      }&d=${serializeDocument({ title: term, saved: false })}&t=${serializeTabs(
        { fullscreen: true }
      )}&e=append`;
      app.appendTab(term, url, { open: true });
    },
    [orgId, searchContext.params.serialized, app]
  );

  const handleSubmitQuestion = useCallback(
    async (text: string = searchTerm) => {
      const data = await exploreContext.ask.submitQuestion(text);
      switch (data?.intent) {
        case "query":
        case "search": {
          const serialized = serializeParams(data.query);
          const url = `/[orgId]/explore?orgId=${orgId}&s=${serialized}&d=${serializeDocument(
            { title: text, saved: false }
          )}&t=${serializeTabs({ fullscreen: true })}&e=append`;
          app.appendTab(text, url, { open: true });
          break;
        }
        case "analysis": {
          const topicName = data.topicName;
          const topic = topics?.find((t) => t.name === topicName);
          if (topic) {
            const url = `/[orgId]/topics/[topicId]?orgId=${orgId}&topicId=${topic.id}`;
            app.appendTab(topic.name, url, { open: true });
          }
          break;
        }
        case "team": {
          const teamUuid = data.teamUuid;
          if (teamUuid) {
            const url = `/[orgId]/teams/[teamUuid]?orgId=${orgId}&teamUuid=${teamUuid}`;
            app.appendTab("Team", url, { open: true });
          } else {
            const url = `/[orgId]/teams?orgId=${orgId}`;
            app.appendTab("Teams", url, { open: true });
          }
          break;
        }
      }
    },
    [orgId, searchTerm, exploreContext.ask, topics, app]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleSubmitQuestion();
      }
    },
    [handleSubmitQuestion]
  );

  const handleOpenEntity = useCallback(
    (entityId: string) => {
      const url = `/[orgId]/explore?orgId=${orgId}&entity=${entityId}`;
      app.appendTab("Entity", url, { open: true });
    },
    [orgId, app]
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
      bodyStyle={{
        paddingTop: 24,
      }}
    >
      <SearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setTimeout(() => setIsFocused(false), 200)}
        inputRef={searchInputRef}
        selectedDigest={null}
        placeholder="Search or Ask a question..."
        enableSuggestions={false}
      />
      <div style={{ marginTop: 8 }}>
        <AutoCompleteResults
          context={exploreContext}
          onSubmitSearch={handleSubmitSearch}
          onSubmitQuestion={handleSubmitQuestion}
          onOpenEntity={handleOpenEntity}
        />
      </div>
    </Modal>
  );
};
